import { DOCUSIGN_ROLE_NAMES, getInternalField } from 'constants/docusignTemplates';

const normalize = (value) => (value || '').trim().toLowerCase();

/**
 * Mirrors the backend validation of ConfigureDocusignTemplateRequest so the user gets
 * inline feedback instead of a 400. Errors block the save, warnings only ask for
 * confirmation: they are configurations the backend accepts but that fail later, when
 * the envelope is actually built.
 *
 * Returns { errors, warnings, hasErrors } where errors is shaped
 * { roles: 'msg', 0: { name, roleOnDocusignTemplate, fields: { 0: { templateName, internalName } } } }
 */
export const validateTemplateConfiguration = ({ roles = [], accountId, templateId }) => {
    const errors = {};
    const warnings = [];

    if (!roles.length) {
        errors.roles = 'At least one role is required';
        return { errors, warnings, hasErrors: true };
    }

    const roleNameCount = {};
    const docusignRoleCount = {};

    roles.forEach((role) => {
        const name = normalize(role.name);
        const docusignRole = normalize(role.roleOnDocusignTemplate);
        if (name) roleNameCount[name] = (roleNameCount[name] || 0) + 1;
        if (docusignRole) docusignRoleCount[docusignRole] = (docusignRoleCount[docusignRole] || 0) + 1;
    });

    roles.forEach((role, roleIndex) => {
        const roleErrors = {};
        const fieldErrors = {};

        if (!(role.name || '').trim()) {
            roleErrors.name = 'Role name cannot be blank';
        } else if (roleNameCount[normalize(role.name)] > 1) {
            roleErrors.name = `Duplicate role name found: ${role.name.trim()}`;
        }

        if (!(role.roleOnDocusignTemplate || '').trim()) {
            roleErrors.roleOnDocusignTemplate = 'Role on DocuSign template cannot be blank';
        } else if (docusignRoleCount[normalize(role.roleOnDocusignTemplate)] > 1) {
            roleErrors.roleOnDocusignTemplate = `Duplicate DocuSign role found: ${role.roleOnDocusignTemplate.trim()}`;
        }

        const fields = role.fields || [];
        const internalNameCount = {};
        const templateNameCount = {};

        fields.forEach((field) => {
            const internalName = normalize(field.internalName);
            const templateName = normalize(field.templateName);
            if (internalName) internalNameCount[internalName] = (internalNameCount[internalName] || 0) + 1;
            if (templateName) templateNameCount[templateName] = (templateNameCount[templateName] || 0) + 1;
        });

        fields.forEach((field, fieldIndex) => {
            const currentFieldErrors = {};
            const roleLabel = (role.name || '').trim() || `#${roleIndex + 1}`;

            if (!field.internalName) {
                currentFieldErrors.internalName = 'Select an internal field';
            } else if (!getInternalField(field.internalName)) {
                currentFieldErrors.internalName = 'Unknown internal field';
            } else if (internalNameCount[normalize(field.internalName)] > 1) {
                currentFieldErrors.internalName = `Duplicate internal field ${field.internalName} found for role ${roleLabel}`;
            }

            if (!(field.templateName || '').trim()) {
                currentFieldErrors.templateName = 'Template field name cannot be blank';
            } else if (templateNameCount[normalize(field.templateName)] > 1) {
                currentFieldErrors.templateName = `Duplicate template field ${field.templateName.trim()} found for role ${roleLabel}`;
            }

            if (Object.keys(currentFieldErrors).length) fieldErrors[fieldIndex] = currentFieldErrors;
        });

        if (Object.keys(fieldErrors).length) roleErrors.fields = fieldErrors;
        if (Object.keys(roleErrors).length) errors[roleIndex] = roleErrors;

        if (!fields.length) {
            warnings.push(`Role "${(role.name || '').trim() || `#${roleIndex + 1}`}" has no mapped fields, so no data will be written for that signer.`);
        }
    });

    DOCUSIGN_ROLE_NAMES.forEach(({ value }) => {
        if (!roles.some((role) => normalize(role.name) === normalize(value))) {
            warnings.push(`There is no "${value}" role. The envelope will be created without that recipient.`);
        }
    });

    if (!(accountId || '').trim()) {
        warnings.push('No DocuSign account is selected. Connect the account from the Docusign tab, otherwise the template stays unlinked and sending fails with "Docusign Template not set".');
    }

    if (!(templateId || '').trim()) {
        warnings.push('No DocuSign template is selected, so sending will fail with "Docusign Template not set".');
    }

    return { errors, warnings, hasErrors: Object.keys(errors).length > 0 };
};

/** Trims exactly what the backend trims, so what we show is what gets stored. */
export const buildConfigurationPayload = ({ roles, accountId, templateId }) => ({
    accountId: (accountId || '').trim(),
    templateId: (templateId || '').trim(),
    roles: (roles || []).map((role) => ({
        name: (role.name || '').trim(),
        roleOnDocusignTemplate: (role.roleOnDocusignTemplate || '').trim(),
        fields: (role.fields || []).map((field) => ({
            templateName: (field.templateName || '').trim(),
            internalName: field.internalName
        }))
    }))
});
